import { decodeJwt } from "jose";
import {
  AUTH_EVENT_ID_HEADER,
  extractErrorShape,
  logAuthEvent,
  newAuthEventId,
} from "@/lib/authLogging";
import { TOKEN } from "@/types/common/ids";
import { broadcast, readLatest } from "@/utils/common/crossTabChannel";
import {
  _resetLogoutGuard,
  isLoggingOut,
  isSessionExpired,
  notifySessionExpired,
  notifyTokenRefreshed,
} from "@/utils/common/logout";
import { classifyAuthErrorBody } from "./authResponses";
import { SessionExpiredError } from "./errors";

const TOKEN_HOST = process.env.NEXT_PUBLIC_AUTH_TOKEN_HOST;

/**
 * Recipe core for `src/lib/api/`. Owns:
 *
 *  - the browser-side `/api/auth/refresh` chokepoint call, intra-tab
 *    coalesced via a module-level promise and inter-tab via a Web Lock;
 *  - the "send once, on 401 refresh and retry once" recipe both
 *    `apiFetchRaw` and `WingmanClient` plug into (`apiFetchWithRefresh`);
 *  - the raw authenticated send (`apiFetchRaw`) that constructs headers,
 *    body, and `next.revalidate` from an option bag and routes through
 *    the recipe;
 *  - shared header / URL / body / logging primitives.
 *
 * The return-shape variants (`apiFetch<T>`, `apiStatus`, …) live in
 * `responseShapes.ts`; `requireAuth` lives in `server.ts`. Public
 * exports come out of `index.ts`.
 */

// ---------------------------------------------------------------------------
// Environment + types
// ---------------------------------------------------------------------------

export const IS_SERVER = globalThis.window === undefined;

export type HttpMethod = "GET" | "PUT" | "POST" | "DELETE" | "PATCH";

/**
 * An access token value or an async function that resolves one on demand
 * (e.g. a server-side Auth0 SDK accessor passed in from a caller that
 * already holds a `getAccessToken` closure).
 */
export type AccessTokenOrAccessor = TOKEN | (() => Promise<TOKEN>);

export interface ApiFetchRawOptions {
  /**
   * Request body. Plain objects are JSON-serialised; `BodyInit` values
   * (FormData, Blob, ReadableStream, etc.) are passed through unchanged
   * and skip the JSON `Content-Type` header.
   */
  body?: unknown;
  headers?: Record<string, string>;
  /**
   * Explicit access-token override (or accessor function). When absent,
   * the browser path falls back to `credentials: "include"` so the
   * session cookie carries auth.
   */
  accessToken?: AccessTokenOrAccessor;
  /**
   * Default `true`. Set to `false` to opt out of 401 refresh+retry — the
   * raw 401 response is returned to the caller.
   */
  retryOn401?: boolean;
  /**
   * Maps onto Next.js's `next: { revalidate }` fetch option. When `0`
   * (default) the request is sent with `cache: "no-store"`.
   */
  revalidateSeconds?: number;
  /** Abort the underlying fetch. */
  signal?: AbortSignal;
}

// ---------------------------------------------------------------------------
// Token accessor
// ---------------------------------------------------------------------------

export async function resolveAccessToken(
  accessToken: AccessTokenOrAccessor | undefined,
): Promise<{ token: TOKEN; hasToken: boolean }> {
  let token: TOKEN = undefined;
  if (typeof accessToken === "function") {
    token = await accessToken();
  } else {
    token = accessToken;
  }
  return { token, hasToken: (token?.length ?? 0) > 0 };
}

// ---------------------------------------------------------------------------
// Header + URL helpers
// ---------------------------------------------------------------------------

export function buildJsonHeaders(
  hasToken: boolean,
  token: TOKEN,
  extra?: Record<string, string>,
): Record<string, string> {
  return hasToken
    ? {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        ...extra,
      }
    : { "Content-Type": "application/json", ...extra };
}

// ---------------------------------------------------------------------------
// Logging helpers
// ---------------------------------------------------------------------------

function getOrgIdFromToken(accessToken: TOKEN): string {
  if (!accessToken || !TOKEN_HOST) return "";
  try {
    const payload = decodeJwt(accessToken);
    const orgId = payload[`${TOKEN_HOST}/org_id`];
    return typeof orgId === "string" ? orgId : "";
  } catch {
    return "";
  }
}

export function logApiError(
  context: string,
  details: {
    url: string;
    method: string;
    status?: number;
    statusText?: string;
    error?: unknown;
    responseText?: string;
    /** Either a JWT (org id is extracted) or undefined. */
    token?: TOKEN;
  },
): void {
  const orgId = getOrgIdFromToken(details.token);
  const orgInfo = orgId ? ` [orgId: ${orgId}]` : "";
  const statusInfo = details.status
    ? ` ${details.status} ${details.statusText ?? ""}`
    : "";
  console.error(
    `[API Error]${orgInfo} ${context}: ${details.method} ${details.url}${statusInfo}`,
    {
      ...(details.responseText ? { response: details.responseText } : {}),
      ...(details.error ? { error: details.error } : {}),
    },
  );
}

// ---------------------------------------------------------------------------
// Response body helpers
// ---------------------------------------------------------------------------

export async function safeReadResponseText(response: Response): Promise<string> {
  try {
    return await response.text();
  } catch {
    return "";
  }
}

export function safeParseJson<T = unknown>(text: string): T | null {
  if (!text || text.trim() === "") return null;
  try {
    return JSON.parse(text) as T;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Browser refresh chokepoint — intra-tab coalesced
// ---------------------------------------------------------------------------

let refreshTokenPromise: Promise<boolean> | null = null;

/**
 * Resets the module-level auth state. Test-only.
 * @internal
 */
export function _resetAuthState(): void {
  _resetLogoutGuard();
  refreshTokenPromise = null;
}

// Cross-tab refresh coordination: hold a Web Lock so sibling tabs queue
// instead of racing Set-Cookie writes on the chunked `__session.*` cookies,
// and skip the POST entirely when a sibling tab just refreshed.
const REFRESH_LOCK_NAME = "gail:auth-refresh";
const RECENT_REFRESH_CHANNEL = "lastRefreshAt";
const RECENT_REFRESH_WINDOW_MS = 10_000;

function readLastRefreshAt(): number {
  const n = Number(readLatest(RECENT_REFRESH_CHANNEL));
  return Number.isFinite(n) ? n : 0;
}

function writeLastRefreshAt(timestamp: number): void {
  // On failure (private mode, quota) this degrades to a redundant POST;
  // the server-side single-flight still serializes.
  broadcast(RECENT_REFRESH_CHANNEL, String(timestamp));
}

async function refreshWithCrossTabLock(): Promise<boolean> {
  const locks = globalThis.navigator?.locks;
  if (!locks?.request) return doRefreshToken();

  return locks.request(REFRESH_LOCK_NAME, async () => {
    if (isSessionExpired()) return false;

    // Reject negative elapsed: a future-dated lastRefreshAt (system clock
    // change, sibling tab on a skewed machine, malformed write) must not
    // freeze refreshes until the clock catches up. Only honor recency when
    // the timestamp lives in a sane past window.
    const elapsed = Date.now() - readLastRefreshAt();
    if (elapsed >= 0 && elapsed < RECENT_REFRESH_WINDOW_MS) {
      logAuthEvent({
        event: "client.refresh_token.cross_tab_skip",
        auth_event_id: newAuthEventId(),
        elapsed_ms: elapsed,
      });
      return true;
    }

    const ok = await doRefreshToken();
    if (ok) writeLastRefreshAt(Date.now());
    return ok;
  });
}

/**
 * Internal: actually hit the refresh chokepoint. Callers should use
 * {@link tryRefreshToken} which adds the intra-tab and cross-tab dedup
 * guards.
 */
async function doRefreshToken(): Promise<boolean> {
  const authEventId = newAuthEventId();
  try {
    const response = await fetch("/api/auth/refresh", {
      method: "POST",
      credentials: "include",
      headers: {
        [AUTH_EVENT_ID_HEADER]: authEventId,
      },
    });

    if (response.ok) {
      logAuthEvent({
        event: "client.refresh_token.ok",
        auth_event_id: authEventId,
      });
      notifyTokenRefreshed();
      return true;
    }

    const responseText = await safeReadResponseText(response);

    if (response.status === 401) {
      logAuthEvent({
        event: "client.refresh_token.session_expired",
        auth_event_id: authEventId,
        status: 401,
        body: responseText.slice(0, 500),
      });
      notifySessionExpired();
      return false;
    }

    logAuthEvent({
      event: "client.refresh_token.non_ok",
      auth_event_id: authEventId,
      status: response.status,
      body: responseText.slice(0, 500),
    });
    return false;
  } catch (error) {
    logAuthEvent({
      event: "client.refresh_token.network_error",
      auth_event_id: authEventId,
      error: extractErrorShape(error),
    });
    return false;
  }
}

/**
 * Browser-side: refresh the access token via `/api/auth/refresh`, with
 * intra-tab concurrent calls coalesced into one in-flight request. Returns
 * `true` if the refresh succeeded.
 *
 * Server callers always get `false`. Recovery through this route happens
 * in the browser, after a 401 response.
 *
 * No-ops (returning `false` without contacting the server) when:
 *  - a logout is already in progress (`isLoggingOut()`); or
 *  - `notifySessionExpired()` has already fired — another
 *    `/api/auth/refresh` call would just produce a second `invalid_grant`
 *    against the already-rotated refresh token.
 */
export async function tryRefreshToken(): Promise<boolean> {
  if (IS_SERVER) return false;
  if (isLoggingOut()) return false;
  if (isSessionExpired()) return false;

  if (refreshTokenPromise) {
    return refreshTokenPromise;
  }

  refreshTokenPromise = refreshWithCrossTabLock().finally(() => {
    refreshTokenPromise = null;
  });

  return refreshTokenPromise;
}

// ---------------------------------------------------------------------------
// The deep recipe: send once, on 401 refresh and retry once.
// ---------------------------------------------------------------------------

/**
 * Run `makeRequest` once. On 401, if refresh is permitted and succeeds,
 * run `makeRequest` again and return the retry's {@link Response}.
 *
 * The returned `Response` always has its body intact and readable once
 * by the caller. A final 401 body is consumed internally for auth-error
 * classification, then a fresh `Response` carrying the original
 * status/headers + the buffered body text is returned. Callers never
 * need to worry about double-reads.
 *
 * If the final 401 carries a `session_expired` / `not_authenticated`
 * body, on the browser `notifySessionExpired()` fires (which raises the
 * session-expired modal); on the server a {@link SessionExpiredError} is
 * thrown so the server-action caller can surface it.
 *
 * `WingmanClient.executeRequest` plugs in here directly so streaming /
 * abort / custom-header logic stays in the client while the retry policy
 * lives in one place.
 *
 * The thunk MUST be safely re-runnable: it will be invoked a second time
 * if the first response is a 401 and the refresh succeeds.
 */
export async function apiFetchWithRefresh(
  makeRequest: () => Promise<Response>,
  options: { retryOn401?: boolean } = {},
): Promise<Response> {
  const retryOn401 = options.retryOn401 ?? !IS_SERVER;
  return runRecipe(makeRequest, retryOn401);
}

async function runRecipe(
  makeRequest: () => Promise<Response>,
  retryOn401: boolean,
): Promise<Response> {
  const response = await makeRequest();

  if (response.status !== 401) {
    return response;
  }

  // 401: read the body once for classification. We'll either retry (and
  // discard this body) or hand it back to the caller via a rebuilt Response.
  const responseText = await safeReadResponseText(response);

  if (isLoggingOut()) {
    return rebuildResponseWithBody(response, responseText);
  }

  if (retryOn401) {
    const refreshed = await tryRefreshToken();
    if (refreshed) {
      // Classify a terminal 401 on the retry too, without refreshing again.
      return runRecipe(makeRequest, false);
    }
    if (isLoggingOut()) {
      return rebuildResponseWithBody(response, responseText);
    }
  }

  // Retry was either skipped (server / explicit opt-out / second pass)
  // or didn't recover the session. If the body says session_expired,
  // surface that to the user via the appropriate channel.
  const reason = classifyAuthErrorBody(responseText);
  if (reason && reason !== "refresh_required") {
    logAuthEvent({
      event: "api.session_expired_response",
      auth_event_id: newAuthEventId(),
      reason,
      is_server: IS_SERVER,
    });
    if (IS_SERVER) {
      throw new SessionExpiredError(reason);
    }
    notifySessionExpired();
  }

  return rebuildResponseWithBody(response, responseText);
}

function rebuildResponseWithBody(
  original: Response,
  bodyText: string,
): Response {
  return new Response(bodyText, {
    status: original.status,
    statusText: original.statusText,
    headers: original.headers,
  });
}

// ---------------------------------------------------------------------------
// Raw authenticated send — top-level fetch with header/body construction.
// ---------------------------------------------------------------------------

/**
 * Send an authenticated request and return the {@link Response}. On 401
 * the browser refresh chokepoint is hit (intra-tab coalesced) and the
 * request is retried once. The caller decides how to consume the body.
 *
 * Used directly by binary callers (file upload/download) and by helpers
 * in `responseShapes.ts` that need access to status / headers / streams.
 */
export async function apiFetchRaw(
  url: string,
  method: HttpMethod = "GET",
  options: ApiFetchRawOptions = {},
): Promise<Response> {
  const init = await buildRequestInit(method, options);

  return apiFetchWithRefresh(() => fetch(url, init), {
    retryOn401: options.retryOn401,
  });
}

async function buildRequestInit(
  method: HttpMethod,
  options: ApiFetchRawOptions,
): Promise<RequestInit> {
  const { token, hasToken } = await resolveAccessToken(options.accessToken);
  const extraHeaders = options.headers ?? {};
  const isJson = options.body !== undefined && !isBodyInit(options.body);

  let headers: Record<string, string>;
  if (isJson) {
    headers = buildJsonHeaders(hasToken, token, extraHeaders);
  } else if (hasToken) {
    headers = { Authorization: `Bearer ${token}`, ...extraHeaders };
  } else {
    headers = { ...extraHeaders };
  }

  const init: RequestInit = {
    method,
    headers,
    credentials: hasToken ? undefined : "include",
    signal: options.signal,
  };

  if (options.body !== undefined) {
    init.body = isJson
      ? JSON.stringify(options.body)
      : (options.body as BodyInit);
  }

  if (options.revalidateSeconds && options.revalidateSeconds > 0) {
    (init as RequestInit & { next?: { revalidate: number } }).next = {
      revalidate: options.revalidateSeconds,
    };
  } else {
    init.cache = "no-store";
  }

  return init;
}

function isBodyInit(body: unknown): body is BodyInit {
  if (typeof body === "string") return true;
  if (body instanceof ArrayBuffer) return true;
  if (typeof FormData !== "undefined" && body instanceof FormData) return true;
  if (typeof Blob !== "undefined" && body instanceof Blob) return true;
  if (typeof URLSearchParams !== "undefined" && body instanceof URLSearchParams)
    return true;
  if (typeof ReadableStream !== "undefined" && body instanceof ReadableStream)
    return true;
  if (ArrayBuffer.isView(body)) return true;
  return false;
}
